import { useMemo, useState } from "react";
import EditModal from "../ui/EditModal";
import { formatDate } from "../../utils/dateUtils";

const SolidFoodTable = ({ data, onUpdate, onDelete }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortDir, setSortDir] = useState("desc");
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sorted = useMemo(() => {
    return data.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortDir]);

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData = sorted.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const toggleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      await onUpdate(editingItem.id, formData);
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (err) {
      console.error("Error saving:", err);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        await onDelete(id);
      } catch (err) {
        console.error("Error deleting:", err);
        alert("Failed to delete entry");
      }
    }
  };

  const editFields = [
    { name: "date", label: "Date & Time", type: "datetime-local", required: true },
    { name: "foodName", label: "Food Name", type: "text", required: true },
    { name: "quantity", label: "Quantity", type: "text", required: true },
    { name: "reactions", label: "Reactions", type: "text" },
    { name: "notes", label: "Notes", type: "textarea", rows: 2 },
  ];

  return (
    <>
      <div className="alert alert-info mb-3">
        <strong>Total Entries:</strong> {sorted.length}
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col" style={{ cursor: "pointer" }} onClick={toggleSort}>
                DateTime {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th scope="col">Food</th>
              <th scope="col">Quantity</th>
              <th scope="col">Reactions</th>
              <th scope="col" style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, index) => (
              <tr key={index}>
                <td>{formatDate(row.date)}</td>
                <td>{row.foodName}</td>
                <td>{row.quantity}</td>
                <td>
                  {row.reactions !== "None" ? (
                    <span className="badge bg-warning">{row.reactions}</span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-xs btn-warning me-1"
                    title="Edit"
                    onClick={() => handleEdit(row)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✎
                  </button>
                  <button
                    className="btn btn-xs btn-danger"
                    title="Delete"
                    onClick={() => handleDelete(row.id)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav aria-label="Page navigation">
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(pageCount)].map((_, idx) => {
              const num = idx + 1;
              return (
                <li key={num} className={`page-item ${num === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>
                    {num}
                  </button>
                </li>
              );
            })}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.min(prev + 1, pageCount))}
                disabled={page === pageCount}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      <EditModal
        isOpen={isModalOpen}
        item={editingItem}
        title="Edit Solid Food Entry"
        fields={editFields}
        onSave={handleSave}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        isLoading={isSaving}
      />
    </>
  );
};

export default SolidFoodTable;
